// src/app/custom-preloading-strategy.service.ts
import { Injectable, PLATFORM_ID, TransferState, inject, makeStateKey } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common'; 

// Define a key for TransferState if you need to pass data from server to browser
const PRELOAD_MODULES_KEY = makeStateKey<string[]>('preloadModules');

@Injectable({ providedIn: 'root' })
export class CustomPreloadingStrategy implements PreloadingStrategy {
    private platformId = inject(PLATFORM_ID);
    private transferState = inject(TransferState);
    private preloadedRoutes: string[] = [];

    preload(route: Route, load: () => Observable<any>): Observable<any> {
        if (isPlatformBrowser(this.platformId)) {
            // On the browser, retrieve preloaded routes from TransferState
            const preloadedFromSSR = this.transferState.get(PRELOAD_MODULES_KEY, []);
            if (preloadedFromSSR.includes(route.path!)) {
                console.log(`Preloading ${route.path} (from SSR hint)`);
                return load();
            }

            // Implement your custom client-side preloading logic here
            // For example, based on user interaction, network conditions, etc.
            if (route.data && route.data['preloadOnBrowser']) {
                console.log(`Preloading ${route.path} (client-side decision)`);
                return load();
            }
        } else {
            // On the server, decide which routes to preload and store in TransferState
            if (route.data && route.data['preloadOnSSR'] && route.path) {
                this.preloadedRoutes.push(route.path);
                // We don't load the module itself on the server, just mark it for preloading on client
                return of(null);
            }
        }
        return of(null); // Don't preload
    }

    // This method will be called on the server to save the state
    ngOnDestroy(): void {
        if (!isPlatformBrowser(this.platformId) && this.preloadedRoutes.length > 0) {
            this.transferState.set(PRELOAD_MODULES_KEY, this.preloadedRoutes);
        }
    }
}